import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FastField, Form, Formik } from "formik";
import * as Yup from "yup";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import { getProductById, getProductList } from "../../api/products";
import { addPOItem, getPOItemById, updatePOItem } from "../../api/purchaseOrders";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { formatCurrency } from "../../services/valueFormatters";
import { buildItemPayload, toNumber } from "./poItemHelpers";

const numberRequired = (message) =>
  Yup.number()
    .transform((value, original) => (original === "" || original == null ? undefined : value))
    .required(message);

const makeSchema = () =>
  Yup.object().shape({
    expenseAccountId: Yup.number().nullable(),
    unitId: numberRequired("La unidad es obligatoria"),
    quantity: Yup.number()
      .required("La cantidad es obligatoria")
      .min(0.0001, "La cantidad debe ser mayor que cero"),
    price: Yup.number()
      .required("El precio es obligatorio")
      .min(0, "El precio no puede ser negativo"),
    taxRate: Yup.number().required("La tasa IVA es obligatoria"),
  });

const makeEmptyValues = (isImport) => ({
  expenseAccount: null,
  expenseAccountId: "",
  quantity: 0,
  unit: null,
  unitId: "",
  concept: "",
  product: null,
  productId: null,
  price: 0,
  workOrderId: "",
  discountPercentage: 0,
  taxRate: isImport ? 0 : 16,
});

export default function PoItemForm({
  id,
  onSubmit,
  onItemsChanged,
  purchaseOrderId,
  companyId,
  isImport = false,
  expenseAccounts = [],
  units = [],
}) {
  const { openSnack } = useSnack();
  const [loading, setLoading] = useState(Boolean(id));
  const [initialValues, setInitialValues] = useState(makeEmptyValues(isImport));

  useEffect(() => {
    if (!id) {
      setInitialValues(makeEmptyValues(isImport));
      setLoading(false);
      return;
    }

    const fetchItem = async () => {
      setLoading(true);
      try {
        const response = await getPOItemById(id);
        const item = response.data;

        let product = item.product || null;
        const productId = item.productId ?? item.product?.id ?? null;
        if (!product && productId) {
          try {
            const productResponse = await getProductById(productId);
            product = productResponse.data;
          } catch {
            // Product lookup is best-effort; form still loads with productId
          }
        }

        setInitialValues({
          expenseAccount: item.expenseAccount || null,
          expenseAccountId: item.expenseAccountId ?? item.expenseAccount?.id ?? "",
          quantity: toNumber(item.quantity),
          unit: item.unit || null,
          unitId: item.unitId ?? item.unit?.id ?? "",
          concept: item.concept || "",
          product,
          productId,
          price: toNumber(item.price),
          workOrderId: item.workOrderId || "",
          discountPercentage: toNumber(item.discountPercentage),
          taxRate: toNumber(item.taxRate, isImport ? 0 : 16),
        });
      } catch (error) {
        openSnack(handleError(error), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [id, isImport, openSnack]);

  const handleSave = async (values) => {
    const payloadValues = {
      ...values,
      expenseAccountId: values.expenseAccount?.id ?? values.expenseAccountId,
      unitId: values.unit?.id ?? values.unitId,
      productId: values.product?.id ?? values.productId,
    };

    try {
      if (!id) {
        await addPOItem(buildItemPayload(payloadValues, purchaseOrderId));
        openSnack("Línea agregada exitosamente", "success");
      } else {
        const payloadToUpdate = {
          id,
          Id: id,
          ...buildItemPayload(payloadValues, purchaseOrderId),
        };
        if (payloadToUpdate.productId === 0) {
          delete payloadToUpdate.productId;
        }
        await updatePOItem(id, payloadToUpdate);
        openSnack("Línea actualizada exitosamente", "success");
      }

      await onSubmit?.();
      onItemsChanged?.();
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={makeSchema()}
      onSubmit={handleSave}
      enableReinitialize
    >
      {({ values, errors, touched, isSubmitting, setFieldValue: setLocalFieldValue }) => {
        const quantity = toNumber(values.quantity);
        const price = toNumber(values.price);
        const discountPercentage = toNumber(values.discountPercentage);
        const taxRate = toNumber(values.taxRate);

        const descuento = quantity * price * (discountPercentage / 100);
        const discountedPrice = quantity * price - descuento;
        const impuestos = discountedPrice * (taxRate / 100);
        const subtotal = discountedPrice * (1 + taxRate / 100);

        return (
          <Form>
            <Grid container spacing={2} sx={{ p: 2 }}>
              <Grid item xs={12} md={6}>
                <FastField
                  name="expenseAccountId"
                  as={DropdownSelector}
                  parentOptions={expenseAccounts}
                  optionLabel={["completeContpaqiCode", "name"]}
                  optionSeparator=" | "
                  label="Actividad"
                  onChange={(_, selected) => {
                    setLocalFieldValue("expenseAccount", selected || null);
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <LGAutocomplete
                  fetchFunction={getProductList}
                  queryParams={{ companyId, pageSize: 20 }}
                  optionLabel="code"
                  value={values.product || null}
                  onChange={(_, value) => {
                    setLocalFieldValue("product", value);
                    setLocalFieldValue("productId", value?.id ?? null);
                    setLocalFieldValue("concept", value?.description ?? "");
                  }}
                  label="Producto"
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <TextField
                  fullWidth
                  label="Concepto"
                  multiline
                  minRows={2}
                  value={values.concept}
                  onChange={(e) => setLocalFieldValue("concept", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Cantidad"
                  type="number"
                  value={values.quantity}
                  onChange={(e) =>
                    setLocalFieldValue(
                      "quantity",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  error={Boolean(touched.quantity && errors.quantity)}
                  helperText={touched.quantity ? errors.quantity : ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FastField
                  name="unitId"
                  as={DropdownSelector}
                  parentOptions={units}
                  optionLabel="code"
                  label="Unidad"
                  onChange={(_, selected) => {
                    setLocalFieldValue("unit", selected || null);
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Precio unitario"
                  type="number"
                  value={values.price}
                  onChange={(e) =>
                    setLocalFieldValue(
                      "price",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  error={Boolean(touched.price && errors.price)}
                  helperText={touched.price ? errors.price : ""}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  label="Descuento (%)"
                  type="number"
                  value={values.discountPercentage}
                  onChange={(e) =>
                    setLocalFieldValue(
                      "discountPercentage",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Tasa IVA</InputLabel>
                  <Select
                    label="Tasa IVA"
                    value={values.taxRate}
                    onChange={(e) => setLocalFieldValue("taxRate", e.target.value)}
                    error={Boolean(touched.taxRate && errors.taxRate)}
                  >
                    {[0, 8, 16].map((rate) => (
                      <MenuItem key={rate} value={rate}>
                        {rate}%
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Orden de trabajo"
                  value={values.workOrderId}
                  onChange={(e) => setLocalFieldValue("workOrderId", e.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, textAlign: "right" }}>
                  <Typography variant="h6">
                    Descuento: {formatCurrency(descuento ? descuento : 0)}
                  </Typography>
                  <Typography variant="h6">
                    Impuestos: {formatCurrency(impuestos)}
                  </Typography>
                  <Typography variant="h6" fontWeight="medium">
                    Subtotal: {formatCurrency(subtotal)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    Guardar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
}
